import React, { useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import { useLocation, useNavigate } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import leafletImage from 'leaflet-image';
import { toPdf } from "./pdfGenerator";
import { closestPointOnSegment, isPointNearPolyline, getSpeedLimitAtPoint } from "../utils/helpers";

const useQuery = () => new URLSearchParams(useLocation().search);

const generateOptions = (start, end) => {
  const options = [];
  for (let i = start; i <= end; i++) {
    options.push(i.toString().padStart(2, "0"));
  }
  return options;
};

const CarSpeed = () => {
  const query = useQuery();
  const navigate = useNavigate();

  const [carNo, setCarNo] = useState(query.get("car") || "");
  const [date, setDate] = useState(query.get("date") || "");
  const [startHour, setStartHour] = useState(query.get("sh") || "00");
  const [startMinute, setStartMinute] = useState(query.get("sm") || "00");
  const [stopHour, setStopHour] = useState(query.get("eh") || "23");
  const [stopMinute, setStopMinute] = useState(query.get("em") || "59");
  const [showOnlyColored, setShowOnlyColored] = useState(query.get("filter") === "true");
  const selectedIndexFromQuery = parseInt(query.get("selected"));
  const zoomFromQuery = parseInt(query.get("zoom"));

  const [carHistoryData, setCarHistoryData] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const mapRef = useRef(null);
  const popupRefs = useRef([]);
  const markerRefs = useRef([]);
  const listRef = useRef(null);
  const hasFitBounds = useRef(false);
  const timestart = `${startHour}:${startMinute}:00`;
  const timestop = `${stopHour}:${stopMinute}:00`;

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/permanentPoints.json`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        const linesOnly = data.filter((item) => item.type === "line");
        if (!hasFitBounds.current) {
          setTracks(linesOnly);
        }
      })
      .catch((err) => console.error("Error loading track data:", err));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams({
      car: carNo,
      date,
      sh: startHour,
      sm: startMinute,
      eh: stopHour,
      em: stopMinute,
      filter: showOnlyColored,
    });

    if (selectedIndex !== null) {
      params.set("selected", selectedIndex);
      params.set("zoom", 16);
    }

    navigate({ search: params.toString() }, { replace: true });
    // 'replace' avoids adding history entries every time you click
  }, [carNo, date, startHour, startMinute, stopHour, stopMinute, showOnlyColored, selectedIndex]);

  useEffect(() => {
    if (selectedIndexFromQuery >= 0 && carHistoryData.length > selectedIndexFromQuery && mapRef.current) {
      const selectedPoint = carHistoryData[selectedIndexFromQuery];
      setSelectedIndex(selectedIndexFromQuery);
      mapRef.current.flyTo([selectedPoint.lat, selectedPoint.lon], zoomFromQuery || 16);
      const popup = popupRefs.current[selectedIndexFromQuery];
      if (popup) popup.openPopup();
    }
  }, [carHistoryData, selectedIndexFromQuery, zoomFromQuery]);

  const updateQueryParams = () => {
    const params = new URLSearchParams({
      car: carNo,
      date,
      sh: startHour,
      sm: startMinute,
      eh: stopHour,
      em: stopMinute,
      filter: showOnlyColored,
      ...(selectedIndex !== null && { selected: selectedIndex, zoom: 16 }),
    });
    navigate({ search: params.toString() });
  };

  const fetchCarData = async () => {
    updateQueryParams();
    try {
      const response = await fetch("https://www.arkadopoulos.eu/gpstracking/getCarHistory.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ carNo, date, timestart, timestop }),
      });
      const data = await response.json();
      if (Array.isArray(data)) {
        setCarHistoryData(data);
      } else {
        setCarHistoryData([]);
      }
    } catch (error) {
      console.error("Error fetching car data:", error);
      setCarHistoryData([]);
    }
  };

  const getMarkerColor = (point) => {
    for (const track of tracks) {
      if (isPointNearPolyline(L.latLng(point.lat, point.lon), track.coordinates, 20)) {
        return point.speed > parseInt(track.speedlimit) + 5 ? "red" : "green";
      }
    }
    return "white";
  };

  const FitBounds = ({ tracks }) => {
    const map = useMap();

    useEffect(() => {
      if (!hasFitBounds.current && tracks.length > 0) {
        const bounds = L.latLngBounds(tracks.flatMap((t) => t.coordinates));
        map.fitBounds(bounds);
        hasFitBounds.current = true; // ✅ Prevent future re-fits
      }
    }, [tracks, map]);

    return null;
  };

  const resetMapView = () => {
    if (!mapRef.current || tracks.length === 0) return;

    const bounds = L.latLngBounds(tracks.flatMap((t) => t.coordinates));
    mapRef.current.fitBounds(bounds);
  };

  

const handlePrintMapPdf = () => {
  const map = mapRef.current;
  if (!map) return;

  leafletImage(map, function (err, canvas) {
    if (err) {
      console.error("Leaflet-image failed:", err);
      return;
    }

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "px",
      format: [canvas.width, canvas.height],
    });

    pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
    pdf.save("map.pdf");
  });
};


  const handlePrintTablePdf = () => {
    if (!filteredData.length) {
      alert("No data to print");
      return;
    }

    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageWidth = pdf.internal.pageSize.getWidth();

    pdf.setFontSize(14);
    pdf.setTextColor(0, 0, 0); // black for title
    pdf.text("Car Speed Report", pageWidth / 2, 15, { align: "center" });
    pdf.setFontSize(10);

    const rowHeight = 8;
    const margin = 10;
    let y = 25;

    filteredData.forEach((point, i) => {
      const lat = Number(point.lat).toFixed(5);
      const lon = Number(point.lon).toFixed(5);
      const speed = point.speed ?? "--";
      const time = point.gpstime ?? "--";
      const limit = getSpeedLimitAtPoint(point, tracks);

      const color = getMarkerColor(point); // "red", "green", or "white"

      // Set text color based on status
      if (color === "red") pdf.setTextColor(255, 0, 0);
      else if (color === "green") pdf.setTextColor(0, 150, 0);
      else pdf.setTextColor(0, 0, 0); // default (white → black text)

      const text = `${i + 1}. Lat: ${lat}, Lon: ${lon} | Speed: ${speed} km/h | Limit: ${limit} km/h | Time: ${time}`;
      pdf.text(text, margin, y);
      y += rowHeight;

      if (y > 280) {
        pdf.addPage();
        y = 15;
      }
    });

    pdf.save("list.pdf");
  };

  const filteredData = showOnlyColored
    ? carHistoryData.filter((p) => {
        const color = getMarkerColor(p);
        return color === "red" || color === "green";
      })
    : carHistoryData;

  const scrollToRow = (index) => {
    if (listRef.current && listRef.current.children[index]) {
      listRef.current.children[index].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  };

  const handleClick = (index) => {
    setSelectedIndex(index);

    const point = filteredData[index];
    if (mapRef.current && point) {
      mapRef.current.flyTo([point.lat, point.lon], 16);
    }

    if (listRef.current && listRef.current.children[index]) {
      listRef.current.children[index].scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    }

    if (markerRefs.current[index]) {
      markerRefs.current[index].openPopup();
    }

    updateQueryParams();
  };

  return (
    <div style={{ display: "flex", height: "100vh" }}>
      <div style={{ flex: "3", display: "flex", flexDirection: "column" }}>
        <div className="controls" style={{ padding: "10px", display: "flex", alignItems: "center", gap: "10px" }}>
          <label>Car No:</label>
          <input type="text" placeholder="Car" value={carNo} onChange={(e) => setCarNo(e.target.value)} style={{ width: "60px" }} />
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />

          <div className="time-input">
            <label>Start Time:</label>
            <select value={startHour} onChange={(e) => setStartHour(e.target.value)}>
              {generateOptions(0, 23).map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))}
            </select>
            :
            <select value={startMinute} onChange={(e) => setStartMinute(e.target.value)}>
              {generateOptions(0, 59).map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>

          <div className="time-input">
            <label>Stop Time:</label>
            <select value={stopHour} onChange={(e) => setStopHour(e.target.value)}>
              {generateOptions(0, 23).map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))}
            </select>
            :
            <select value={stopMinute} onChange={(e) => setStopMinute(e.target.value)}>
              {generateOptions(0, 59).map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>

          <label>
            <input type="checkbox" checked={showOnlyColored} onChange={() => setShowOnlyColored(!showOnlyColored)} />
            Show only red/green points
          </label>

          <button onClick={fetchCarData}>Fetch History</button>

          <button onClick={handlePrintMapPdf}>Export Map to PDF</button>
          <button onClick={handlePrintTablePdf}>Export List to PDF</button>
          <button onClick={resetMapView}>Reset View</button>
        </div>

        <MapContainer preferCanvas={false} style={{ flex: 1 }} center={[39.66, 20.85]} zoom={14} ref={mapRef} scrollWheelZoom>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          {/*  <FitBounds tracks={tracks} /> */}

          {tracks.map((track, i) => (
            <Polyline key={i} positions={track.coordinates} color={track.color} weight={track.weight} />
          ))}

          {filteredData.map((point, i) => (
            <Marker
              key={i}
              position={[point.lat, point.lon]}
              ref={(el) => {
                if (el) markerRefs.current[i] = el;
              }}
              eventHandlers={{
                click: () => handleClick(i),
              }}
              icon={L.divIcon({
                className: "",
                iconSize: selectedIndex === i ? [16, 16] : [10, 10],
                iconAnchor: selectedIndex === i ? [8, 8] : [5, 5],
                html: `<div style="width:100%; height:100%; background:${getMarkerColor(point)}; border-radius:50%;"></div>`,
              })}>
              <Popup>
                Speed: {point.speed} km/h
                <br />
                Limit: {getSpeedLimitAtPoint(point, tracks)}
                <br />
                Time: {point.gpstime}
                <br />
                Lat: {point.lat}
                <br />
                Lon: {point.lon}
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      <div ref={listRef} style={{ flex: "1", overflowY: "scroll", padding: "10px", borderLeft: "1px solid #ccc" }}>
        <div style={{ whiteSpace: "nowrap" }}>
          {filteredData.map((point, i) => (
            <div
              key={i}
              style={{
                color: getMarkerColor(point),
                marginBottom: "6px",
                cursor: "pointer",
                fontWeight: selectedIndex === i ? "bold" : "normal",
                backgroundColor: selectedIndex === i ? "#f0f0f0" : "transparent",
              }}
              onClick={() => handleClick(i)}>
              {i + 1}. {point.lat}, {point.lon} | {point.speed} km/h | {point.gpstime} | {getSpeedLimitAtPoint(point, tracks)} km/h
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CarSpeed;
